import React, {
  useEffect,
  useCallback,
  useState,
  useContext,
  useRef,
} from "react";
import {
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  View as RNView,
  Modal,
  Pressable,
  ActivityIndicator,
  TextInput,
  Image,
  FlatList,
  Platform,
} from "react-native";
import FeatherIcon from "react-native-vector-icons/Feather";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../../../utils/useTranslation";
import DeviceOrientationContext from "../../../../components/context/DeviceOrientationContext";
import { View } from "../../../../components/shared/Themed";
import {
  getOrganizations,
  listAllPortalStates,
  updateOrganization,
} from "../../../../redux/dispatcher/portalDispatcher";
import {
  setOrganizationCategoryFilter,
  setOrganizationPage,
  setOrganizationSortBy,
  setOrganizationSearchQuery,
} from "../../../../redux/reducers/portalReducer";
import AddEditOrganizationModal from "./modal";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  navy: "#0F1B2D",
  navyMid: "#1A2E46",
  navyLight: "#243B55",
  accent: "#61B8FF",
  accentDark: "#3A9FE8",
  success: "#0E9F6E",
  successBg: "#EDFAF4",
  warning: "#F59E0B",
  warningBg: "#FFF8EC",
  danger: "#E74C3C",
  dangerBg: "#FEF0EE",
  surface: "#FFFFFF",
  surfaceAlt: "#F7F9FC",
  border: "#E4EAF2",
  borderLight: "#F0F4FA",
  text: "#0F1B2D",
  textMid: "#4A5568",
  textMuted: "#8FA3BF",
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────
interface Organization {
  _id: string;
  name: string;
  description?: string;
  cardCode?: string;
  prefix?: string;
  logo?: string;
  logoUrl?: string;
  domains?: string[];
  states?: string[];
  contacts?: { address: string; email: string; phoneNumber: string }[];
  sales?: { name: string; email: string }[];
  isActive?: boolean;
  is_active?: boolean;
  pricingEnabled?: boolean;
  pricingModel?: string;
  pricingSalesEmail?: string | null;
  showAvailableStock?: boolean;
  showOnOrderStock?: boolean;
  showStockOnTransit?: boolean;
  showGrowers?: boolean;
  createdAt?: string;
}

type ViewMode = "grid" | "list";
type CategoryFilter = "all" | "active" | "inactive";
type SortOption = "name-asc" | "name-desc" | "created-desc" | "created-asc";

// ─── Sort / Filter helpers ────────────────────────────────────────────────────

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "name-asc", label: "Name A–Z" },
  { value: "name-desc", label: "Name Z–A" },
  { value: "created-desc", label: "Newest First" },
  { value: "created-asc", label: "Oldest First" },
];

const CATEGORY_FILTERS: { value: CategoryFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const statusColor = (org: Organization): string => {
  if (org.isActive === true) return C.success;
  if (org.isActive === false) return C.danger;
  return C.textMuted;
};

const statusLabel = (org: Organization): string => {
  if (org.status === "active" || org.is_active === true) return "Active";
  if (org.status === "inactive" || org.is_active === false) return "Inactive";
  return "Unknown";
};

const getInitials = (name: string): string =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

// ─── Context Menu Modal ───────────────────────────────────────────────────────
interface ContextMenuProps {
  visible: boolean;
  onClose: () => void;
  isSuperUser: boolean;
  onViewUsers: () => void;
  onViewOrders: () => void;
  onViewGrowers: () => void;
  onViewProducts: () => void;
  onViewDashboard: () => void;
  onAssignProducts: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  visible,
  onClose,
  isSuperUser,
  onViewUsers,
  onViewOrders,
  onViewGrowers,
  onViewProducts,
  onViewDashboard,
  onAssignProducts,
}) => {
  const items: { label: string; icon: string; action: () => void }[] = [
    ...(isSuperUser
      ? [{ label: "View Users", icon: "users", action: onViewUsers }]
      : []),
    { label: "View Growers", icon: "grid", action: onViewGrowers },
    { label: "View Orders", icon: "file-text", action: onViewOrders },
    { label: "View Products", icon: "package", action: onViewProducts },
    { label: "View Dashboard", icon: "bar-chart-2", action: onViewDashboard },
    {
      label: "Assign Products",
      icon: "check-square",
      action: onAssignProducts,
    },
  ];

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={CM.backdrop} onPress={onClose}>
        <Pressable style={CM.menu} onPress={(e) => e.stopPropagation()}>
          <RNView style={CM.header}>
            <Text style={CM.headerTitle}>Actions</Text>
            <TouchableOpacity onPress={onClose}>
              <FeatherIcon name="x" size={16} color={C.textMuted} />
            </TouchableOpacity>
          </RNView>
          <RNView style={CM.divider} />
          {items.map((item) => (
            <TouchableOpacity
              key={item.label}
              style={CM.item}
              onPress={() => {
                onClose();
                item.action();
              }}
              activeOpacity={0.7}
            >
              <RNView style={CM.itemIcon}>
                <FeatherIcon
                  name={item.icon as any}
                  size={15}
                  color={C.accent}
                />
              </RNView>
              <Text style={CM.itemLabel}>{item.label}</Text>
              <FeatherIcon name="chevron-right" size={14} color={C.textMuted} />
            </TouchableOpacity>
          ))}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

interface DeleteConfirmModalProps {
  visible: boolean;
  orgName?: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  visible,
  orgName,
  loading,
  onConfirm,
  onCancel,
}) => (
  <Modal
    visible={visible}
    transparent
    animationType="fade"
    onRequestClose={onCancel}
  >
    <Pressable style={DC.backdrop} onPress={onCancel}>
      <Pressable style={DC.card} onPress={(e) => e.stopPropagation()}>
        <RNView style={DC.iconWrap}>
          <FeatherIcon name="alert-triangle" size={28} color={C.danger} />
        </RNView>
        <Text style={DC.title}>Delete Organisation?</Text>
        <Text style={DC.body}>
          Are you sure you want to delete{" "}
          <Text style={{ fontWeight: "800" }}>{orgName}</Text>?{"\n"}This action
          cannot be undone.
        </Text>
        <RNView style={DC.actions}>
          <TouchableOpacity
            style={DC.cancelBtn}
            onPress={onCancel}
            disabled={loading}
          >
            <Text style={DC.cancelText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[DC.deleteBtn, loading && { opacity: 0.6 }]}
            onPress={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Text style={DC.deleteText}>Delete</Text>
            )}
          </TouchableOpacity>
        </RNView>
      </Pressable>
    </Pressable>
  </Modal>
);

// ─── Sort Picker Modal ────────────────────────────────────────────────────────

interface SortPickerProps {
  visible: boolean;
  current: SortOption;
  onSelect: (v: SortOption) => void;
  onClose: () => void;
}

const SortPicker: React.FC<SortPickerProps> = ({
  visible,
  current,
  onSelect,
  onClose,
}) => (
  <Modal
    visible={visible}
    transparent
    animationType="slide"
    onRequestClose={onClose}
  >
    <Pressable style={SP.backdrop} onPress={onClose}>
      <Pressable style={SP.sheet} onPress={(e) => e.stopPropagation()}>
        <RNView style={SP.handle} />
        <Text style={SP.title}>Sort By</Text>
        {SORT_OPTIONS.map((opt) => (
          <TouchableOpacity
            key={opt.value}
            style={[SP.option, current === opt.value && SP.optionActive]}
            onPress={() => {
              onSelect(opt.value);
              onClose();
            }}
            activeOpacity={0.7}
          >
            <Text
              style={[
                SP.optionText,
                current === opt.value && SP.optionTextActive,
              ]}
            >
              {opt.label}
            </Text>
            {current === opt.value && (
              <FeatherIcon name="check" size={15} color={C.accent} />
            )}
          </TouchableOpacity>
        ))}
      </Pressable>
    </Pressable>
  </Modal>
);

// ─── Organisation Card ────────────────────────────────────────────────────────

interface OrgCardProps {
  org: Organization;
  isSuperUser: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onView: () => void;
  viewMode: ViewMode;
}

const OrgCard: React.FC<OrgCardProps> = ({
  org,
  isSuperUser,
  onEdit,
  onDelete,
  onView,
  viewMode,
}) => {
  const sc = statusColor(org);
  const sl = statusLabel(org);
  const isGrid = viewMode === "grid";

  if (!isGrid) {
    return (
      <RNView style={OC.listRow}>
        <RNView style={[OC.avatar, { backgroundColor: C.accent + "18" }]}>
          {org.logoUrl ? (
            <Image source={{ uri: org.logoUrl }} style={OC.avatarImg} />
          ) : (
            <Text style={OC.avatarInitials}>{getInitials(org.name)}</Text>
          )}
        </RNView>

        <RNView style={{ flex: 1 }}>
          <RNView
            style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
          >
            <Text style={OC.listName} numberOfLines={1}>
              {org.name}
            </Text>
            <RNView style={[OC.statusDot, { backgroundColor: sc }]} />
          </RNView>
          {!!org.description && (
            <Text style={OC.listDesc} numberOfLines={1}>
              {org.description}
            </Text>
          )}
          {!!org.cardCode && <Text style={OC.listMeta}>{org.cardCode}</Text>}
        </RNView>

        <RNView style={OC.listActions}>
          <TouchableOpacity style={OC.actionBtn} onPress={onView}>
            <FeatherIcon name="more-horizontal" size={16} color={C.textMid} />
          </TouchableOpacity>
          {isSuperUser && (
            <TouchableOpacity style={OC.actionBtn} onPress={onEdit}>
              <FeatherIcon name="edit-2" size={15} color={C.accent} />
            </TouchableOpacity>
          )}
        </RNView>
      </RNView>
    );
  }

  return (
    <RNView style={OC.gridCard}>
      <RNView style={[OC.statusBar, { backgroundColor: sc }]} />
      <RNView style={OC.gridContent}>
        <RNView style={OC.gridTop}>
          <RNView style={[OC.gridAvatar, { backgroundColor: C.accent + "18" }]}>
            {org.logoUrl ? (
              <Image source={{ uri: org.logoUrl }} style={OC.gridAvatarImg} />
            ) : (
              <Text style={OC.gridInitials}>{getInitials(org.name)}</Text>
            )}
          </RNView>
          <RNView style={OC.gridActions}>
            <TouchableOpacity style={OC.gridActionBtn} onPress={onView}>
              <FeatherIcon name="more-horizontal" size={15} color={C.textMid} />
            </TouchableOpacity>
            {isSuperUser && (
              <TouchableOpacity style={OC.gridActionBtn} onPress={onEdit}>
                <FeatherIcon name="edit-2" size={14} color={C.accent} />
              </TouchableOpacity>
            )}
          </RNView>
        </RNView>

        <Text style={OC.gridName} numberOfLines={2}>
          {org.name}
        </Text>
        {!!org.description && (
          <Text style={OC.gridDesc} numberOfLines={2}>
            {org.description}
          </Text>
        )}

        <RNView style={OC.gridFooter}>
          <RNView
            style={[
              OC.statusChip,
              { backgroundColor: sc + "18", borderColor: sc + "40" },
            ]}
          >
            <RNView style={[OC.statusChipDot, { backgroundColor: sc }]} />
            <Text style={[OC.statusChipText, { color: sc }]}>{sl}</Text>
          </RNView>
          {!!org.cardCode && (
            <Text style={OC.gridCardCode}>{org.cardCode}</Text>
          )}
        </RNView>

        {org.domains && org.domains.length > 0 && (
          <RNView style={OC.domainsRow}>
            {org.domains.slice(0, 2).map((d, i) => (
              <RNView key={i} style={OC.domainChip}>
                <Text style={OC.domainChipText} numberOfLines={1}>
                  {d}
                </Text>
              </RNView>
            ))}
            {org.domains.length > 2 && (
              <Text style={OC.domainMore}>+{org.domains.length - 2}</Text>
            )}
          </RNView>
        )}
      </RNView>
    </RNView>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────

interface NavigationProp {
  navigate: (screen: string, params?: Record<string, unknown>) => void;
}

interface Props {
  navigation?: NavigationProp;
}

const CustomerOrganizationsListScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const { isSmallDevice, isMobileDevice } = useContext(
    DeviceOrientationContext,
  );

  const {
    organizations: fetchedOrganizations,
    loading,
    loadingActionOrganization,
    page,
    pageSize,
    totalCount,
    totalPages,
    categoryFilter,
    searchQuery,
    sortBy,
  } = useSelector((state: any) => state.portal.admin.organizations);

  // ── Local UI state ────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [localSearch, setLocalSearch] = useState<string>(searchQuery ?? "");
  const [sortPickerVisible, setSortPickerVisible] = useState<boolean>(false);
  const [contextMenuVisible, setContextMenuVisible] = useState<boolean>(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [deleteVisible, setDeleteVisible] = useState<boolean>(false);
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);
  const [addEditVisible, setAddEditVisible] = useState<boolean>(false);

  const isSuperUser = true;
  const isMobile = isSmallDevice || isMobileDevice;

  // ── Debounced search ──────────────────────────────────────────────────────
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback(
    (text: string) => {
      setLocalSearch(text);
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = setTimeout(() => {
        dispatch(setOrganizationSearchQuery(text));
        dispatch(setOrganizationPage(1));
      }, 400);
    },
    [dispatch],
  );

  const handleClearSearch = useCallback(() => {
    setLocalSearch("");
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    dispatch(setOrganizationSearchQuery(""));
    dispatch(setOrganizationPage(1));
  }, [dispatch]);

  // ── Fetch on filter / pagination changes ──────────────────────────────────
  useEffect(() => {
    dispatch(getOrganizations());
  }, [page, pageSize, categoryFilter, searchQuery, sortBy]);

  useEffect(() => {
    dispatch(listAllPortalStates());
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleView = useCallback((org: Organization) => {
    setSelectedOrg(org);
    setContextMenuVisible(true);
  }, []);

  const handleEdit = useCallback((org: Organization) => {
    setSelectedOrg(org);
    setAddEditVisible(true);
  }, []);

  const handleDelete = useCallback((org: Organization) => {
    setOrgToDelete(org);
    setDeleteVisible(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!orgToDelete) return;
    // dispatch(deleteOrganization(orgToDelete._id)).then(() => {
    //   dispatch(getOrganizations());
    // });
    setDeleteVisible(false);
    setOrgToDelete(null);
  }, [orgToDelete]);

  const handleCancelDelete = useCallback(() => {
    setDeleteVisible(false);
    setOrgToDelete(null);
  }, []);

  const handleSortSelect = useCallback(
    (value: SortOption) => {
      dispatch(setOrganizationSortBy(value));
      dispatch(setOrganizationPage(1));
    },
    [dispatch],
  );

  const handleCategoryFilter = useCallback(
    (value: CategoryFilter) => {
      dispatch(setOrganizationCategoryFilter(value));
      dispatch(setOrganizationPage(1));
    },
    [dispatch],
  );

  const handlePrevPage = useCallback(() => {
    if (page > 1) {
      dispatch(setOrganizationPage(page - 1));
    }
  }, [dispatch, page]);

  const handleNextPage = useCallback(() => {
    if (page < totalPages) {
      dispatch(setOrganizationPage(page + 1));
    }
  }, [dispatch, page, totalPages]);

  const navigate = useCallback(
    (screen: string, params?: Record<string, unknown>) => {
      navigation?.navigate(screen, params);
    },
    [navigation],
  );

  const handleSave = async (data: any) => {
    console.log(data, "data");
    if (selectedOrg) {
      dispatch(updateOrganization({ id: selectedOrg._id, ...data }));
    } else {
      // dispatch(createOrganization(data))
    }
    setAddEditVisible(false);
    setSelectedOrg(null);
  };

  // ── Render item ───────────────────────────────────────────────────────────
  const renderItem = useCallback(
    ({ item }: { item: Organization }) => (
      <RNView style={viewMode === "grid" && !isMobile ? { flex: 1 } : {}}>
        <OrgCard
          org={item}
          isSuperUser={isSuperUser}
          viewMode={viewMode}
          onView={() => handleView(item)}
          onEdit={() => handleEdit(item)}
          onDelete={() => handleDelete(item)}
        />
      </RNView>
    ),
    [viewMode, isMobile, isSuperUser, handleView, handleEdit, handleDelete],
  );

  const keyExtractor = useCallback((item: Organization) => item._id, []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <View style={S.root}>
      {/* ══ CONTROLS ════════════════════════════════════════════════════════ */}
      <RNView style={S.controlsBar}>
        {/* Search */}
        <RNView style={S.searchWrap}>
          <FeatherIcon name="search" size={14} color={C.textMuted} />
          <TextInput
            style={S.searchInput}
            value={localSearch}
            onChangeText={handleSearchChange}
            placeholder="Search organisations…"
            placeholderTextColor={C.textMuted}
            returnKeyType="search"
            autoCorrect={false}
            autoCapitalize="none"
          />
          {localSearch.length > 0 && (
            <TouchableOpacity
              onPress={handleClearSearch}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <FeatherIcon name="x" size={14} color={C.textMuted} />
            </TouchableOpacity>
          )}
        </RNView>

        {/* Sort + View toggle */}
        <RNView style={S.controlsRow}>
          <TouchableOpacity
            style={S.sortBtn}
            onPress={() => setSortPickerVisible(true)}
            activeOpacity={0.7}
          >
            <FeatherIcon name="sliders" size={13} color={C.textMid} />
            <Text style={S.sortBtnText}>
              {SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? "Sort"}
            </Text>
            <FeatherIcon name="chevron-down" size={13} color={C.textMuted} />
          </TouchableOpacity>

          <RNView style={S.viewToggle}>
            <TouchableOpacity
              style={[
                S.viewToggleBtn,
                viewMode === "grid" && S.viewToggleBtnActive,
              ]}
              onPress={() => setViewMode("grid")}
            >
              <FeatherIcon
                name="grid"
                size={15}
                color={viewMode === "grid" ? "#fff" : C.textMuted}
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                S.viewToggleBtn,
                viewMode === "list" && S.viewToggleBtnActive,
              ]}
              onPress={() => setViewMode("list")}
            >
              <FeatherIcon
                name="list"
                size={15}
                color={viewMode === "list" ? "#fff" : C.textMuted}
              />
            </TouchableOpacity>
          </RNView>
        </RNView>

        {/* Category chips */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={S.categoryScroll}
        >
          {CATEGORY_FILTERS.map((cat) => {
            const isActive = categoryFilter === cat.value;
            return (
              <TouchableOpacity
                key={cat.value}
                style={[S.catChip, isActive && S.catChipActive]}
                // FIX: was calling action creator without dispatch
                onPress={() => handleCategoryFilter(cat.value)}
                activeOpacity={0.75}
              >
                <Text style={[S.catChipText, isActive && S.catChipTextActive]}>
                  {cat.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </RNView>

      {/* ══ BODY ════════════════════════════════════════════════════════════ */}
      {loading ? (
        <RNView style={S.loadingWrap}>
          <ActivityIndicator size="large" color={C.accent} />
          <Text style={S.loadingText}>Loading organizations</Text>
        </RNView>
      ) : fetchedOrganizations.length === 0 ? (
        <RNView style={S.emptyState}>
          <RNView style={S.emptyIconWrap}>
            <FeatherIcon name="briefcase" size={28} color={C.accent} />
          </RNView>
          <Text style={S.emptyTitle}>No organizations found</Text>
          <Text style={S.emptySubtitle}>
            {localSearch
              ? "Try adjusting your search or filters"
              : "Get started by creating your first organisation"}
          </Text>
          {!localSearch && (
            <TouchableOpacity
              style={S.emptyCreateBtn}
              onPress={() => {
                setSelectedOrg(null);
                setAddEditVisible(true);
              }}
            >
              <FeatherIcon name="plus" size={14} color="#fff" />
              <Text style={S.emptyCreateBtnText}>Add Organizations</Text>
            </TouchableOpacity>
          )}
        </RNView>
      ) : (
        <FlatList<Organization>
          data={fetchedOrganizations}
          keyExtractor={keyExtractor}
          contentContainerStyle={{ padding: 12, paddingBottom: 32 }}
          numColumns={viewMode === "grid" && !isMobile ? 2 : 1}
          key={viewMode === "grid" && !isMobile ? "grid2" : "list1"}
          columnWrapperStyle={
            viewMode === "grid" && !isMobile ? { gap: 12 } : undefined
          }
          showsVerticalScrollIndicator={false}
          renderItem={renderItem}
          ListFooterComponent={
            totalPages > 1 ? (
              <RNView style={S.pagination}>
                <TouchableOpacity
                  style={[S.pageBtn, page === 1 && S.pageBtnDisabled]}
                  onPress={handlePrevPage}
                  disabled={page === 1}
                >
                  <FeatherIcon
                    name="chevron-left"
                    size={16}
                    color={page === 1 ? C.textMuted : C.navy}
                  />
                </TouchableOpacity>
                <Text style={S.pageInfo}>
                  Page {page} of {totalPages}
                </Text>
                <TouchableOpacity
                  style={[S.pageBtn, page === totalPages && S.pageBtnDisabled]}
                  onPress={handleNextPage}
                  disabled={page === totalPages}
                >
                  <FeatherIcon
                    name="chevron-right"
                    size={16}
                    color={page === totalPages ? C.textMuted : C.navy}
                  />
                </TouchableOpacity>
              </RNView>
            ) : null
          }
        />
      )}

      {/* ══ MODALS ══════════════════════════════════════════════════════════ */}
      <SortPicker
        visible={sortPickerVisible}
        current={sortBy}
        onSelect={handleSortSelect}
        onClose={() => setSortPickerVisible(false)}
      />

      <ContextMenu
        visible={contextMenuVisible}
        onClose={() => setContextMenuVisible(false)}
        isSuperUser={isSuperUser}
        onViewUsers={() =>
          navigate("OrganizationMembers", {
            orgId: selectedOrg?._id,
            orgName: selectedOrg?.name,
          })
        }
        onViewOrders={() =>
          navigate("AdminOrders", {
            orgId: selectedOrg?._id,
            orgName: selectedOrg?.name,
          })
        }
        onViewGrowers={() =>
          navigate("AdminGrowers", {
            orgId: selectedOrg?._id,
            orgName: selectedOrg?.name,
          })
        }
        onViewProducts={() =>
          navigate("AdminProducts", {
            organization: selectedOrg as unknown as Record<string, unknown>,
          })
        }
        onViewDashboard={() =>
          navigate("AdminDashboard", {
            organization: selectedOrg as unknown as Record<string, unknown>,
          })
        }
        onAssignProducts={() =>
          navigate("CustomerAssignProducts", {
            organization: selectedOrg as unknown as Record<string, unknown>,
          })
        }
      />

      <DeleteConfirmModal
        visible={deleteVisible}
        orgName={orgToDelete?.name}
        loading={loadingActionOrganization}
        onConfirm={confirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Add/Edit modal — import AddEditOrganizationModal */}
      <AddEditOrganizationModal
        visible={addEditVisible}
        organization={selectedOrg}
        onClose={() => {
          setAddEditVisible(false);
          setSelectedOrg(null);
        }}
        onSave={handleSave}
      />
    </View>
  );
};

export default CustomerOrganizationsListScreen;

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = StyleSheet.create({
  root: { flex: 1, backgroundColor: C.surfaceAlt },

  controlsBar: {
    backgroundColor: C.surface,
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    gap: 10,
  },
  searchWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: C.surfaceAlt,
    borderRadius: 10,
    paddingHorizontal: 12,
    height: 40,
    borderWidth: 1.5,
    borderColor: C.border,
  },
  searchInput: { flex: 1, fontSize: 14, color: C.text },

  controlsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sortBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 7,
    paddingHorizontal: 12,
    backgroundColor: C.surfaceAlt,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: C.border,
  },
  sortBtnText: { fontSize: 12, fontWeight: "600", color: C.textMid },
  viewToggle: {
    flexDirection: "row",
    backgroundColor: C.surfaceAlt,
    borderRadius: 8,
    padding: 3,
    borderWidth: 1.5,
    borderColor: C.border,
    gap: 2,
  },
  viewToggleBtn: {
    width: 30,
    height: 30,
    borderRadius: 6,
    justifyContent: "center",
    alignItems: "center",
  },
  viewToggleBtnActive: { backgroundColor: C.navy },

  categoryScroll: { gap: 8, paddingBottom: 4 },
  catChip: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 20,
    backgroundColor: C.surfaceAlt,
    borderWidth: 1.5,
    borderColor: C.border,
  },
  catChipActive: { backgroundColor: C.navy, borderColor: C.navy },
  catChipText: { fontSize: 12, fontWeight: "600", color: C.textMid },
  catChipTextActive: { color: "#fff" },

  loadingWrap: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 60,
    gap: 12,
  },
  loadingText: { fontSize: 14, color: C.textMuted },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 80,
    gap: 10,
    paddingHorizontal: 32,
  },
  emptyIconWrap: {
    width: 64,
    height: 64,
    borderRadius: 20,
    backgroundColor: C.accent + "18",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 4,
  },
  emptyTitle: { fontSize: 18, fontWeight: "800", color: C.text },
  emptySubtitle: { fontSize: 13, color: C.textMuted, textAlign: "center" },
  emptyCreateBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: C.accent,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 20,
    marginTop: 8,
  },
  emptyCreateBtnText: { fontSize: 13, fontWeight: "800", color: "#fff" },

  pagination: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  pageBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: C.surface,
    borderWidth: 1.5,
    borderColor: C.border,
    justifyContent: "center",
    alignItems: "center",
  },
  pageBtnDisabled: { opacity: 0.35 },
  pageInfo: { fontSize: 13, fontWeight: "600", color: C.textMid },
});

const DC = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(10,20,35,0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  card: {
    width: "100%",
    maxWidth: 340,
    backgroundColor: C.surface,
    borderRadius: 20,
    padding: 24,
    alignItems: "center",
    gap: 12,
  },
  iconWrap: {
    width: 60,
    height: 60,
    borderRadius: 18,
    backgroundColor: C.dangerBg,
    justifyContent: "center",
    alignItems: "center",
  },
  title: { fontSize: 18, fontWeight: "800", color: C.text },
  body: { fontSize: 13, color: C.textMid, textAlign: "center", lineHeight: 20 },
  actions: { flexDirection: "row", gap: 10, width: "100%", marginTop: 4 },
  cancelBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: C.border,
    justifyContent: "center",
    alignItems: "center",
  },
  cancelText: { fontSize: 14, fontWeight: "700", color: C.textMid },
  deleteBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    backgroundColor: C.danger,
    justifyContent: "center",
    alignItems: "center",
  },
  deleteText: { fontSize: 14, fontWeight: "800", color: "#fff" },
});

const CM = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(10,20,35,0.4)",
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  menu: {
    width: "100%",
    maxWidth: 320,
    backgroundColor: C.surface,
    borderRadius: 16,
    overflow: "hidden",
    shadowColor: C.navy,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 24,
    elevation: 12,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 18,
    paddingVertical: 14,
  },
  headerTitle: { fontSize: 15, fontWeight: "800", color: C.text },
  divider: { height: 1, backgroundColor: C.borderLight },
  item: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 18,
    paddingVertical: 13,
    gap: 12,
  },
  itemIcon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    backgroundColor: C.accent + "18",
    justifyContent: "center",
    alignItems: "center",
  },
  itemLabel: { flex: 1, fontSize: 14, fontWeight: "600", color: C.text },
});

const SP = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(10,20,35,0.45)",
    justifyContent: "flex-end",
  },
  sheet: {
    backgroundColor: C.surface,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    paddingTop: 12,
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: C.border,
    alignSelf: "center",
    marginBottom: 16,
  },
  title: { fontSize: 16, fontWeight: "800", color: C.text, marginBottom: 12 },
  option: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 13,
    paddingHorizontal: 4,
    borderRadius: 8,
  },
  optionActive: { backgroundColor: C.accent + "10" },
  optionText: { fontSize: 14, fontWeight: "600", color: C.textMid },
  optionTextActive: { color: C.accent, fontWeight: "700" },
});

const OC = StyleSheet.create({
  listRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: C.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: C.borderLight,
    padding: 12,
    marginBottom: 8,
    gap: 12,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  avatarImg: { width: 44, height: 44, borderRadius: 12 },
  avatarInitials: { fontSize: 16, fontWeight: "800", color: C.accent },
  statusDot: { width: 8, height: 8, borderRadius: 4 },
  listName: { fontSize: 14, fontWeight: "700", color: C.text, flex: 1 },
  listDesc: { fontSize: 12, color: C.textMid, marginTop: 2 },
  listMeta: { fontSize: 11, color: C.textMuted, marginTop: 2 },
  listActions: { flexDirection: "row", gap: 6, flexShrink: 0 },
  actionBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: C.surfaceAlt,
    justifyContent: "center",
    alignItems: "center",
  },
  gridCard: {
    backgroundColor: C.surface,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: C.borderLight,
    overflow: "hidden",
    marginBottom: 12,
    shadowColor: C.navy,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  statusBar: { height: 4, width: "100%" },
  gridContent: { padding: 14 },
  gridTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
  },
  gridAvatar: {
    width: 46,
    height: 46,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  gridAvatarImg: { width: 46, height: 46, borderRadius: 12 },
  gridInitials: { fontSize: 18, fontWeight: "800", color: C.accent },
  gridActions: { flexDirection: "row", gap: 6 },
  gridActionBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    backgroundColor: C.surfaceAlt,
    justifyContent: "center",
    alignItems: "center",
  },
  gridName: {
    fontSize: 15,
    fontWeight: "800",
    color: C.text,
    marginBottom: 4,
    lineHeight: 20,
  },
  gridDesc: {
    fontSize: 12,
    color: C.textMid,
    lineHeight: 17,
    marginBottom: 10,
  },
  gridFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  statusChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 20,
    borderWidth: 1,
  },
  statusChipDot: { width: 6, height: 6, borderRadius: 3 },
  statusChipText: { fontSize: 10, fontWeight: "700" },
  gridCardCode: { fontSize: 11, color: C.textMuted, fontWeight: "600" },
  domainsRow: { flexDirection: "row", flexWrap: "wrap", gap: 5, marginTop: 2 },
  domainChip: {
    backgroundColor: C.navyLight + "14",
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  domainChipText: { fontSize: 10, color: C.navyLight, fontWeight: "600" },
  domainMore: {
    fontSize: 10,
    color: C.textMuted,
    fontWeight: "700",
    alignSelf: "center",
  },
});
